/**
 * Phone number utility functions for standardized phone display logic
 * 
 * Implements the priority logic: mobilePhone → homePhone → workPhone
 * This ensures consistent phone number display across all platforms
 */

import { formatPhoneNumber } from '../common/format-functions';

/**
 * Phone data interface for customer/member objects
 */
export interface PhoneData {
  /** Mobile phone number */
  mobilePhone?: string;
  /** Home phone number */
  homePhone?: string;
  /** Work phone number */
  workPhone?: string;
}

/**
 * Gets the primary phone number using priority logic: mobile → home → work
 * 
 * @param phoneData - Object containing phone number fields
 * @returns The primary phone number or empty string if none available
 * 
 * @example
 * ```typescript
 * const customer = { mobilePhone: '+1234567890', homePhone: '+0987654321' };
 * const primaryPhone = getPrimaryPhoneNumber(customer); // Returns '+1234567890'
 * ```
 */
export const getPrimaryPhoneNumber = (phoneData: PhoneData): string => {
  if (phoneData?.mobilePhone) {
    return phoneData.mobilePhone;
  }

  if (phoneData?.homePhone) {
    return phoneData.homePhone;
  }

  if (phoneData?.workPhone) {
    return phoneData.workPhone;
  }

  return '';
};

/**
 * Gets the formatted primary phone number using priority logic
 * 
 * @param phoneData - Object containing phone number fields
 * @returns Formatted phone number or empty string if none available
 * 
 * @example
 * ```typescript
 * const customer = { mobilePhone: '1234567890' };
 * const formatted = getFormattedPrimaryPhoneNumber(customer); // Returns '123 456-7890'
 * ```
 */
export const getFormattedPrimaryPhoneNumber = (phoneData: PhoneData): string => {
  const primaryPhone = getPrimaryPhoneNumber(phoneData);
  return primaryPhone ? formatPhoneNumber(primaryPhone) : '';
};

/**
 * Checks if any phone number is available
 * 
 * @param phoneData - Object containing phone number fields
 * @returns True if at least one phone number exists
 */
export const hasPhoneNumber = (phoneData: PhoneData): boolean => {
  return !!(phoneData?.mobilePhone || phoneData?.homePhone || phoneData?.workPhone);
};

/**
 * Gets all available phone numbers in priority order
 * 
 * @param phoneData - Object containing phone number fields
 * @returns Array of available phone numbers in priority order
 */
export const getAllPhoneNumbers = (phoneData: PhoneData): string[] => {
  const phones: string[] = [];

  if (phoneData?.mobilePhone) {
    phones.push(phoneData.mobilePhone);
  }

  if (phoneData?.homePhone) {
    phones.push(phoneData.homePhone);
  }

  if (phoneData?.workPhone) {
    phones.push(phoneData.workPhone);
  }

  return phones;
};

/**
 * Gets phone number type label for the primary phone
 * 
 * @param phoneData - Object containing phone number fields
 * @returns Phone type label ('mobile', 'home', 'work', or 'none')
 */
export const getPrimaryPhoneType = (phoneData: PhoneData): 'mobile' | 'home' | 'work' | 'none' => {
  if (phoneData?.mobilePhone) {
    return 'mobile';
  }

  if (phoneData?.homePhone) {
    return 'home';
  }

  if (phoneData?.workPhone) {
    return 'work';
  }

  return 'none';
}; 